// =============================================================================
// src/features/reputation/reputation-trend.ts
//
// Serie historica do Reputation Index com hover interpolado + leitura por teclado.
// Porte FIEL de assets/reputation.js: drawTrend 95-98 e initTrendInteraction 100-180.
// Preserva: padding 8/14/8, VB 600x150, dominio Y 40-100, lerp linear, throttle por
//   requestAnimationFrame, clamps de tooltip (46px), setas/Home/End e aria-live.
//
// O legado usava `initTrendInteraction._done` para nao religar; aqui um flag de
//   modulo (trendInteractionBound) garante a mesma idempotencia.
// SRP: so a serie historica e sua interacao. Sem outros charts.
// =============================================================================

import { qs } from '../../app/dom';
import type { ReputationAreaMeta } from './reputation-types';
import { buildReputationAreaPath, byId } from './reputation-charts';
import { formatReputationNumber, reputationBand } from './reputation-data';
import { getReputationTrend, getReputationTrendLabels } from './reputation-live-store';

/** Amostra interpolada da serie num X virtual (valor + posicao no viewBox). */
interface TrendSample {
  readonly vbX: number;
  readonly value: number;
  readonly vbY: number;
  readonly pos: number;
}

/** Guarda contra religar a interacao (replica initTrendInteraction._done). */
let trendInteractionBound = false;

/** Desenha a serie e liga a interacao — porte FIEL de `drawTrend` (reputation.js 95-98). */
export function drawReputationTrend(): void {
  const series = getReputationTrend();
  const meta = buildReputationAreaPath('repTrendLine', 'repTrendArea', series, 600, 150, 8, 14, 8, 40, 100);
  if (meta) initTrendInteraction(meta, series);
}

/** Interpolacao linear — porte FIEL de `lerp` (reputation.js 118). */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Liga hover (pointer) + teclado a serie historica. Porte FIEL de
 * `initTrendInteraction` (reputation.js 100-180); idempotente por modulo.
 */
function initTrendInteraction(meta: ReputationAreaMeta, data: readonly number[]): void {
  const plot = document.getElementById('repTrendPlot');
  if (!plot || trendInteractionBound) return;
  trendInteractionBound = true;

  const refs = resolveTrendRefs(plot);
  const xs = meta.coords.map((c) => c[0]);
  const n = data.length;

  const sample = (relX: number, rect: DOMRect): TrendSample => sampleTrend(relX, rect, meta, data, xs);
  const paint = (s: TrendSample, rect: DOMRect): void => paintTrend(s, rect, meta, refs);

  bindTrendPointer(plot, sample, paint);
  bindTrendKeyboard(plot, xs, data, n, meta, refs, paint);
}

/** Elementos auxiliares do plot da serie (guide/dot/tooltip/marker/live). */
interface TrendRefs {
  readonly guide: HTMLElement;
  readonly dot: HTMLElement;
  readonly tip: HTMLElement;
  readonly tipVal: HTMLElement;
  readonly tipTime: HTMLElement;
  readonly mark: SVGElement | null;
  readonly live: HTMLElement | null;
}

/** Resolve as referencias do plot — porte das linhas 105-111 de reputation.js. */
function resolveTrendRefs(plot: HTMLElement): TrendRefs {
  const tip = qs<HTMLElement>('.rep-evo-tip', plot);
  if (!tip) throw new Error('reputation-trend: tooltip ausente, esperado .rep-evo-tip em #repTrendPlot');
  return {
    guide: requireChild(plot, '.rep-evo-guide'),
    dot: requireChild(plot, '.rep-evo-dot'),
    tip,
    tipVal: requireChild(tip, '.rep-evo-tip-val'),
    tipTime: requireChild(tip, '.rep-evo-tip-time'),
    mark: document.getElementById('repTrendMark') as SVGElement | null,
    live: document.getElementById('repTrendLive'),
  };
}

/** Resolve um filho obrigatorio ou lanca com o seletor ofensivo (boundary check). */
function requireChild(root: ParentNode, selector: string): HTMLElement {
  const el = qs<HTMLElement>(selector, root);
  if (!el) throw new Error(`reputation-trend: elemento ausente seletor=${JSON.stringify(selector)}`);
  return el;
}

/** Interpola a amostra no X relativo — porte FIEL de `sample` (reputation.js 119-127). */
function sampleTrend(
  relX: number, rect: DOMRect, meta: ReputationAreaMeta, data: readonly number[], xs: readonly number[],
): TrendSample {
  const n = data.length;
  const minX = xs[0] ?? meta.padX;
  const maxX = xs[n - 1] ?? meta.w;
  let vbX = (relX / rect.width) * meta.w;
  if (vbX < minX) vbX = minX; else if (vbX > maxX) vbX = maxX;
  let i = Math.floor((vbX - meta.padX) / meta.step);
  if (i < 0) i = 0; else if (i > n - 2) i = n - 2;
  const xi = xs[i] ?? minX;
  const xn = xs[i + 1] ?? maxX;
  let t = (vbX - xi) / (xn - xi);
  /* v8 ignore next -- vbX clampado em [xi,xn] e i em [0,n-2] ⇒ t∈[0,1]; clamps defensivos CLAUDE.md rule 11 */
  if (t < 0) t = 0; else if (t > 1) t = 1;
  const yi = meta.coords[i]?.[1] ?? 0;
  const yn = meta.coords[i + 1]?.[1] ?? yi;
  return { vbX, value: lerp(data[i] ?? 0, data[i + 1] ?? (data[i] ?? 0), t), vbY: lerp(yi, yn, t), pos: i + t };
}

/** Posiciona guia/dot/tooltip e a marca vertical — porte FIEL de `paint` (reputation.js 128-139). */
function paintTrend(s: TrendSample, rect: DOMRect, meta: ReputationAreaMeta, refs: TrendRefs): void {
  const xPx = (s.vbX / meta.w) * rect.width;
  const yPx = (s.vbY / meta.h) * rect.height;
  refs.guide.style.transform = `translateX(${xPx.toFixed(1)}px)`;
  refs.dot.style.transform = `translate(${xPx.toFixed(1)}px,${yPx.toFixed(1)}px)`;
  refs.tip.style.left = `${Math.max(46, Math.min(rect.width - 46, xPx)).toFixed(1)}px`;
  refs.tip.style.top = `${yPx.toFixed(1)}px`;
  refs.tip.style.transform = (yPx < 46) ? 'translate(-50%, 14px)' : 'translate(-50%, calc(-100% - 14px))';
  refs.tipVal.textContent = formatReputationNumber(s.value);
  refs.tipTime.textContent = reputationBand(s.value).name;
  if (refs.mark) {
    refs.mark.setAttribute('x1', s.vbX.toFixed(1));
    refs.mark.setAttribute('x2', s.vbX.toFixed(1));
  }
}

/** Liga pointermove/down/enter/leave com throttle por rAF — porte de reputation.js 141-157. */
function bindTrendPointer(
  plot: HTMLElement,
  sample: (relX: number, rect: DOMRect) => TrendSample,
  paint: (s: TrendSample, rect: DOMRect) => void,
): void {
  let pendingX: number | null = null;
  let rafId = 0;
  const onMove = (e: PointerEvent): void => {
    pendingX = e.clientX;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      const rect = plot.getBoundingClientRect();
      if (!rect.width || pendingX === null) return;
      let relX = pendingX - rect.left;
      if (relX < 0) relX = 0; else if (relX > rect.width) relX = rect.width;
      paint(sample(relX, rect), rect);
    });
  };
  plot.addEventListener('pointermove', onMove);
  plot.addEventListener('pointerdown', onMove);
  plot.addEventListener('pointerenter', () => plot.classList.add('is-hover'));
  plot.addEventListener('pointerleave', () => plot.classList.remove('is-hover'));
}

/** Liga setas/Home/End para navegar a serie por teclado — porte de reputation.js 159-179. */
function bindTrendKeyboard(
  plot: HTMLElement,
  xs: readonly number[],
  data: readonly number[],
  n: number,
  meta: ReputationAreaMeta,
  refs: TrendRefs,
  paint: (s: TrendSample, rect: DOMRect) => void,
): void {
  let kbIndex = n - 1;
  const kbShow = (idx: number): void => {
    kbIndex = Math.max(0, Math.min(n - 1, idx));
    const rect = plot.getBoundingClientRect();
    if (!rect.width) return;
    const relX = ((xs[kbIndex] ?? 0) / meta.w) * rect.width;
    plot.classList.add('is-hover');
    paint(sampleTrend(relX, rect, meta, data, xs), rect);
    const txt = `${formatReputationNumber(data[kbIndex] ?? 0)}, ${reputationBand(data[kbIndex] ?? 0).name}, ${getReputationTrendLabels()[kbIndex] ?? ''}`;
    plot.setAttribute('aria-valuenow', String(kbIndex));
    plot.setAttribute('aria-valuetext', txt);
    if (refs.live) refs.live.textContent = txt;
  };
  // Le kbIndex por getter (nao por snapshot) para que setas consecutivas acumulem,
  // igual ao closure compartilhado do legado (reputation.js 159-178).
  plot.addEventListener('keydown', (e) => handleTrendKey(e, () => kbIndex, n, kbShow));
  plot.addEventListener('blur', () => plot.classList.remove('is-hover'));
}

/** Roteia uma tecla de navegacao para kbShow — porte FIEL do keydown (reputation.js 172-178). */
function handleTrendKey(e: KeyboardEvent, readIndex: () => number, n: number, kbShow: (idx: number) => void): void {
  const k = e.key;
  const kbIndex = readIndex();
  if (k === 'ArrowRight' || k === 'ArrowUp') { e.preventDefault(); kbShow(kbIndex + 1); }
  else if (k === 'ArrowLeft' || k === 'ArrowDown') { e.preventDefault(); kbShow(kbIndex - 1); }
  else if (k === 'Home') { e.preventDefault(); kbShow(0); }
  else if (k === 'End') { e.preventDefault(); kbShow(n - 1); }
}

/** Re-exporta byId para o orquestrador (boundary unico). */
export { byId };
